import { useRef, useEffect, useState, useCallback } from 'react';
import { useGameStore, useIsMyTurn } from '../../store/gameStore';
import { HexTile, VertexCoord, EdgeCoord } from '../../../shared/types';
import { hexToPixel, getHexVertices, getHexEdges, vertexToPixel, edgeToPixel } from '../../../shared/utils';
import './Board2D.css';

// テクスチャ画像のマッピング
const TERRAIN_TEXTURES: Record<string, string> = {
  hills: '/assets/standard-theme/brick.png',
  forest: '/assets/standard-theme/wood.png',
  fields: '/assets/standard-theme/wheat.png',
  pasture: '/assets/standard-theme/sheep.png',
  mountains: '/assets/standard-theme/ore.png',
  desert: '/assets/standard-theme/desert.png',
};

// 数字の確率表示用ドット
const NUMBER_DOTS: Record<number, number> = {
  2: 1, 3: 2, 4: 3, 5: 4, 6: 5,
  8: 5, 9: 4, 10: 3, 11: 2, 12: 1,
};

interface Board2DProps {
  onVertexClick?: (vertex: VertexCoord) => void;
  onEdgeClick?: (edge: EdgeCoord) => void;
}

export default function Board2D({ onVertexClick, onEdgeClick }: Board2DProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { gameState, selectedAction, sendAction } = useGameStore();
  const isMyTurn = useIsMyTurn();
  
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMouse, setLastMouse] = useState({ x: 0, y: 0 });
  const [textures, setTextures] = useState<Record<string, HTMLImageElement>>({});
  const [hoveredVertex] = useState<VertexCoord | null>(null);
  const [hoveredEdge] = useState<EdgeCoord | null>(null);

  const HEX_SIZE = 60; // ピクセル単位

  // テクスチャをロード
  useEffect(() => {
    const loadTextures = async () => {
      const loaded: Record<string, HTMLImageElement> = {};
      
      for (const [terrain, path] of Object.entries(TERRAIN_TEXTURES)) {
        const img = new Image();
        img.src = path;
        await new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
        loaded[terrain] = img;
      }
      
      setTextures(loaded);
    };
    
    loadTextures();
  }, []);

  // キャンバスサイズを調整
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const resize = () => {
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  // ボードを描画
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !gameState) return;

    const dpr = window.devicePixelRatio;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    
    // キャンバスをクリア
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);

    // 中心オフセット
    const centerX = canvas.width / dpr / 2 + offset.x;
    const centerY = canvas.height / dpr / 2 + offset.y;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.scale(scale, scale);

    // 海の背景
    ctx.fillStyle = '#1e88e5';
    ctx.beginPath();
    ctx.arc(0, 0, HEX_SIZE * 5.5, 0, Math.PI * 2);
    ctx.fill();

    // ヘックスタイルを描画
    gameState.hexTiles.forEach((tile) => {
      drawHexTile(ctx, tile);
    });

    // 道路を描画
    gameState.roads.forEach((road) => {
      const player = gameState.players.find(p => p.id === road.playerId);
      if (player) {
        drawRoad(ctx, road.edge, player.color);
      }
    });

    // 開拓地と都市を描画
    gameState.settlements.forEach((settlement) => {
      const player = gameState.players.find(p => p.id === settlement.playerId);
      if (player) {
        drawSettlement(ctx, settlement.vertex, player.color, settlement.isCity);
      }
    });

    // ホバー表示
    if (hoveredVertex && selectedAction === 'settlement') {
      drawVertexHighlight(ctx, hoveredVertex);
    }
    if (hoveredEdge && selectedAction === 'road') {
      drawEdgeHighlight(ctx, hoveredEdge);
    }

    ctx.restore();
  }, [gameState, textures, scale, offset, hoveredVertex, hoveredEdge, selectedAction]);

  // 六角形タイルを描画
  const drawHexTile = (ctx: CanvasRenderingContext2D, tile: HexTile) => {
    const pos = hexToPixel(tile.coord, HEX_SIZE);
    
    ctx.save();
    ctx.translate(pos.x, pos.y);

    // 六角形のパスを作成（pointy-top）
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2;
      const x = Math.cos(angle) * HEX_SIZE;
      const y = Math.sin(angle) * HEX_SIZE;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();

    // テクスチャまたは色で塗りつぶし
    const texture = textures[tile.terrain];
    if (texture && texture.complete) {
      ctx.save();
      ctx.clip();
      ctx.drawImage(
        texture,
        -HEX_SIZE,
        -HEX_SIZE,
        HEX_SIZE * 2,
        HEX_SIZE * 2
      );
      ctx.restore();
    } else {
      // フォールバック色
      const colors: Record<string, string> = {
        hills: '#c45a2c',
        forest: '#228b22',
        fields: '#daa520',
        pasture: '#90ee90',
        mountains: '#708090',
        desert: '#f4e4bc',
      };
      ctx.fillStyle = colors[tile.terrain] || '#888';
      ctx.fill();
    }

    // 枠線
    ctx.strokeStyle = '#2c1810';
    ctx.lineWidth = 2;
    ctx.stroke();

    // 数字トークン（砂漠以外）
    if (tile.number) {
      drawNumberToken(ctx, tile.number);
    }

    // 盗賊
    if (tile.hasRobber) {
      drawRobber(ctx);
    }

    ctx.restore();
  };

  // 数字トークンを描画
  const drawNumberToken = (ctx: CanvasRenderingContext2D, num: number) => {
    const isRed = num === 6 || num === 8;
    const radius = 18;

    // 背景円
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.fillStyle = '#fef9e7';
    ctx.fill();
    ctx.strokeStyle = '#5d4e37';
    ctx.lineWidth = 2;
    ctx.stroke();

    // 数字
    ctx.font = `bold ${isRed ? 20 : 18}px 'Segoe UI', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = isRed ? '#dc3545' : '#333';
    ctx.fillText(String(num), 0, -2);

    // ドット
    const dots = NUMBER_DOTS[num] || 0;
    ctx.fillStyle = isRed ? '#dc3545' : '#666';
    const dotY = 10;
    const dotSpacing = 4;
    const startX = -((dots - 1) * dotSpacing) / 2;
    
    for (let i = 0; i < dots; i++) {
      ctx.beginPath();
      ctx.arc(startX + i * dotSpacing, dotY, 2, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  // 盗賊を描画
  const drawRobber = (ctx: CanvasRenderingContext2D) => {
    ctx.save();
    ctx.translate(0, -5);
    
    // 体
    ctx.fillStyle = '#1a1a1a';
    ctx.beginPath();
    ctx.ellipse(0, 15, 10, 14, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // 頭
    ctx.beginPath();
    ctx.arc(0, 0, 10, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
  };

  // 道路を描画
  const drawRoad = (ctx: CanvasRenderingContext2D, edge: EdgeCoord, color: string) => {
    const pos = edgeToPixel(edge, HEX_SIZE);
    const angle = getEdgeAngle(edge);
    
    ctx.save();
    ctx.translate(pos.x, pos.y);
    ctx.rotate(angle);
    
    // 道路の形状
    ctx.fillStyle = getPlayerColor(color);
    ctx.strokeStyle = '#2c1810';
    ctx.lineWidth = 1;
    
    const width = HEX_SIZE * 0.5;
    const height = 8;
    
    ctx.beginPath();
    ctx.roundRect(-width / 2, -height / 2, width, height, 3);
    ctx.fill();
    ctx.stroke();
    
    ctx.restore();
  };

  // 開拓地/都市を描画
  const drawSettlement = (ctx: CanvasRenderingContext2D, vertex: VertexCoord, color: string, isCity: boolean) => {
    const pos = vertexToPixel(vertex, HEX_SIZE);
    const playerColor = getPlayerColor(color);
    
    ctx.save();
    ctx.translate(pos.x, pos.y);
    
    if (isCity) {
      // 都市（大きな建物）
      ctx.fillStyle = playerColor;
      ctx.strokeStyle = '#2c1810';
      ctx.lineWidth = 2;
      
      ctx.beginPath();
      ctx.moveTo(-12, 8);
      ctx.lineTo(-12, -4);
      ctx.lineTo(-6, -12);
      ctx.lineTo(0, -4);
      ctx.lineTo(0, -8);
      ctx.lineTo(6, -16);
      ctx.lineTo(12, -8);
      ctx.lineTo(12, 8);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    } else {
      // 開拓地（家）
      ctx.fillStyle = playerColor;
      ctx.strokeStyle = '#2c1810';
      ctx.lineWidth = 2;
      
      ctx.beginPath();
      ctx.moveTo(-10, 8);
      ctx.lineTo(-10, -2);
      ctx.lineTo(0, -12);
      ctx.lineTo(10, -2);
      ctx.lineTo(10, 8);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    
    ctx.restore();
  };

  // 頂点のハイライト
  const drawVertexHighlight = (ctx: CanvasRenderingContext2D, vertex: VertexCoord) => {
    const pos = vertexToPixel(vertex, HEX_SIZE);
    
    ctx.save();
    ctx.translate(pos.x, pos.y);
    
    ctx.beginPath();
    ctx.arc(0, 0, 12, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 0, 0.5)';
    ctx.fill();
    ctx.strokeStyle = '#ffc107';
    ctx.lineWidth = 3;
    ctx.stroke();
    
    ctx.restore();
  };

  // 辺のハイライト
  const drawEdgeHighlight = (ctx: CanvasRenderingContext2D, edge: EdgeCoord) => {
    const pos = edgeToPixel(edge, HEX_SIZE);
    const angle = getEdgeAngle(edge);
    
    ctx.save();
    ctx.translate(pos.x, pos.y);
    ctx.rotate(angle);
    
    ctx.fillStyle = 'rgba(255, 255, 0, 0.5)';
    ctx.strokeStyle = '#ffc107';
    ctx.lineWidth = 2;
    
    const width = HEX_SIZE * 0.5;
    const height = 12;
    
    ctx.beginPath();
    ctx.roundRect(-width / 2, -height / 2, width, height, 3);
    ctx.fill();
    ctx.stroke();
    
    ctx.restore();
  };

  // 辺の角度を取得
  const getEdgeAngle = (edge: EdgeCoord): number => {
    switch (edge.direction) {
      case 'NE': return -Math.PI / 3;
      case 'E': return 0;
      case 'SE': return Math.PI / 3;
      case 'SW': return -Math.PI / 3;
      case 'W': return 0;
      case 'NW': return Math.PI / 3;
      default: return 0;
    }
  };

  // プレイヤーカラーを取得
  const getPlayerColor = (color: string): string => {
    const colors: Record<string, string> = {
      red: '#e74c3c',
      blue: '#3498db',
      orange: '#e67e22',
      white: '#ecf0f1',
      green: '#27ae60',
      brown: '#8b4513',
    };
    return colors[color] || '#888';
  };

  // 描画ループ
  useEffect(() => {
    draw();
  }, [draw]);

  // マウスイベント
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setLastMouse({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      const dx = e.clientX - lastMouse.x;
      const dy = e.clientY - lastMouse.y;
      setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setLastMouse({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.min(Math.max(prev * delta, 0.5), 2));
  };

  // クリックイベント
  const handleClick = (e: React.MouseEvent) => {
    if (!gameState || !isMyTurn) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2 - offset.x) / scale;
    const y = (e.clientY - rect.top - rect.height / 2 - offset.y) / scale;
    
    // 配置フェーズかチェック
    const isSetupPhase = gameState.phase === 'setup_round_1' || gameState.phase === 'setup_round_2';
    
    if (isSetupPhase || selectedAction === 'settlement' || selectedAction === 'city') {
      // 頂点クリック処理
      const vertex = findNearestVertex(x, y, HEX_SIZE);
      if (vertex && onVertexClick) {
        onVertexClick(vertex);
      } else if (vertex) {
        // 配置アクション
        if (isSetupPhase) {
          sendAction({ type: 'place_settlement', vertex });
        } else if (selectedAction === 'settlement') {
          sendAction({ type: 'build_settlement', vertex });
        } else if (selectedAction === 'city') {
          sendAction({ type: 'build_city', vertex });
        }
      }
    }
    
    if (selectedAction === 'road') {
      // 辺クリック処理
      const edge = findNearestEdge(x, y, HEX_SIZE);
      if (edge && onEdgeClick) {
        onEdgeClick(edge);
      } else if (edge) {
        const isSetup = gameState.phase === 'setup_round_1' || gameState.phase === 'setup_round_2';
        if (isSetup) {
          sendAction({ type: 'place_road', edge });
        } else {
          sendAction({ type: 'build_road', edge });
        }
      }
    }
  };

  // 最も近い頂点を見つける
  const findNearestVertex = (x: number, y: number, hexSize: number): VertexCoord | null => {
    if (!gameState) return null;
    
    const threshold = 15;
    let nearest: VertexCoord | null = null;
    let minDist = Infinity;
    
    for (const tile of gameState.hexTiles) {
      const vertices = getHexVertices(tile.coord);
      for (const v of vertices) {
        const pos = vertexToPixel(v, hexSize);
        const dist = Math.sqrt((pos.x - x) ** 2 + (pos.y - y) ** 2);
        if (dist < threshold && dist < minDist) {
          minDist = dist;
          nearest = v;
        }
      }
    }
    
    return nearest;
  };

  // 最も近い辺を見つける
  const findNearestEdge = (x: number, y: number, hexSize: number): EdgeCoord | null => {
    if (!gameState) return null;
    
    const threshold = 20;
    let nearest: EdgeCoord | null = null;
    let minDist = Infinity;
    
    for (const tile of gameState.hexTiles) {
      const edges = getHexEdges(tile.coord);
      for (const e of edges) {
        const pos = edgeToPixel(e, hexSize);
        const dist = Math.sqrt((pos.x - x) ** 2 + (pos.y - y) ** 2);
        if (dist < threshold && dist < minDist) {
          minDist = dist;
          nearest = e;
        }
      }
    }
    
    return nearest;
  };

  return (
    <div ref={containerRef} className="board-2d-container">
      <canvas
        ref={canvasRef}
        className="board-2d-canvas"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onClick={handleClick}
      />
      
      {/* ズームコントロール */}
      <div className="board-controls">
        <button onClick={() => setScale(s => Math.min(s * 1.2, 2))}>+</button>
        <button onClick={() => setScale(s => Math.max(s * 0.8, 0.5))}>−</button>
        <button onClick={() => { setScale(1); setOffset({ x: 0, y: 0 }); }}>⟲</button>
      </div>
    </div>
  );
}
